// Angular
import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  ActivatedRoute,
  Router,
  NavigationStart,
} from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
// ApplicationInsights
import { ApplicationInsights, Util } from '@microsoft/applicationinsights-web';
// RxJS
import { Subscription } from 'rxjs';
// Environment
import { environment } from 'src/environments/environment';
// Enums
import { RoleType } from '../../../shared/enums/role-type.enum';

export enum SeverityLevel {
  Verbose = 0,
  Information = 1,
  Warning = 2,
  Error = 3,
  Critical = 4,
}

@Injectable({
  providedIn: 'root'
})
export class ApplicationInsightsService {
  private routerSubscription: Subscription;
  private _properties: { [name: string]: string } = {};
  private _turnedOn = false;

  private appInsights = new ApplicationInsights({
    config: {
      instrumentationKey: environment.ApplicationInsightsInstrumentationKey,
      disableTelemetry:
        environment.DisableApplicationInsights === 'false' ? false : true,
      disableFetchTracking: false,
      disableCorrelationHeaders: false,
      enableCorsCorrelation: true,
    },
  });

  /**
   *Creates an instance of ApplicationInsightsService.
   * @param {Router} router
   * @memberof ApplicationInsightsService
   */
  constructor(private router: Router, private route: ActivatedRoute) { }

  turnOn(): void {
    if (!this._turnedOn) {
      this._turnedOn = true;
      this.appInsights.loadAppInsights();

      const telemetryInitializer = (envelope) => {
        envelope.tags['ai.cloud.role'] = 'NewHire.UI';
      };
      this.appInsights.addTelemetryInitializer(telemetryInitializer);

      this.routerSubscription = this.router.events.subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.startTrackPage(event.url);
        }
      });
    }
  }

  turnOff(): void {
    this._turnedOn = false;
    this.appInsights.addTelemetryInitializer(() => false);
    this.routerSubscription.unsubscribe();
  }

  setUserId(userId: string, userName: string, userRole: RoleType): void {
    this._properties['Email'] = userId;
    this._properties['Name'] = userName;
    this._properties['Role'] = RoleType[userRole];

    this.appInsights.setAuthenticatedUserContext(userId);
  }

  setQuoteId(quoteId: string): void {
    this._properties['QuoteId'] = quoteId;
  }

  setGroupId(groupId: number) {
    this._properties['GroupId'] = groupId.toString();
  }

  setCarrierProperties(carrierId: number, carrierName: string) {
    this._properties['CarrierId'] = carrierId.toString();
    this._properties['CarrierName'] = carrierName.toString();
  }

  clearCarrierProperties(): void {
    delete this._properties['CarrierId'];
    delete this._properties['CarrierName'];
  }

  clearUserId(): void {
    delete this._properties['Email'];
    this.appInsights.clearAuthenticatedUserContext();
  }

  /**
   * Manually trigger an immediate send of all telemetry still in the buffer.
   * @param {boolean} [async=true]
   * @memberof Initialization
   */
  flush(): void {
    this.appInsights.flush();
  }

  // https://github.com/Microsoft/ApplicationInsights-JS/blob/master/API-reference.md#trackpageview
  /**
   * Log a bag of performance information via the customProperties field.
   * @param {IPageViewPerformanceTelemetry} pageViewPerformance
   * @memberof Initialization
   */
  logPageView(
    name?: string,
    uri?: string,
    additionalProperties?: { [name: string]: string },
  ): void {
    const properties: { [name: string]: string } = {
      ...this._properties,
      ...additionalProperties,
    };
    try {
      this.appInsights.trackPageView({ name, uri, properties });
      this.appInsights.trackPageViewPerformance({ name, uri, properties });
    } catch (ex) {
      console.warn('Angular application insights Error [trackEvent]: ', ex);
    }
  }

  // https://github.com/Microsoft/ApplicationInsights-JS/blob/master/API-reference.md#trackevent
  /**
   * Log a user action or other occurrence.
   * @param {IEventTelemetry} event
   * @param {ICustomProperties} [customProperties]
   * @memberof Initialization
   */
  trackEvent(
    name?: string,
    additionalProperties?: { [name: string]: string },
  ): void {
    const properties: { [name: string]: string } = {
      ...this._properties,
      ...additionalProperties,
    };
    try {
      this.appInsights.trackEvent({ name, properties });
    } catch (ex) {
      console.warn('Angular application insights Error [trackEvent]: ', ex);
    }
  }

  /**
   * Starts the timer for tracking a page load time. Use this instead of `trackPageView` if you want to control when the page view timer starts and stops,
   * but don't want to calculate the duration yourself. This method doesn't send any telemetry. Call `stopTrackPage` to log the end of the page view
   * and send the event.
   * @param name A string that identifies this item, unique within this HTML document. Defaults to the document title.
   */
  startTrackPage(name?: string): void {
    try {
      this.appInsights.context.telemetryTrace.traceID = Util.generateW3CId();
      this.appInsights.context.telemetryTrace.name = name;

      this.appInsights.startTrackPage(name);
      console.log('start tracking page:: ' + name);
    } catch (ex) {
      console.warn('Angular application insights Error [startTrackPage]: ', ex);
    }
  }

  /**
   * Stops the timer that was started by calling `startTrackPage` and sends the page view load time telemetry with the specified properties and measurements.
   * The duration of the page view will be the time between calling `startTrackPage` and `stopTrackPage`.
   * @param   name  The string you used as the name in startTrackPage. Defaults to the document title.
   * @param   url   String - a relative or absolute URL that identifies the page or other item. Defaults to the window location.
   * @param   properties  map[string, string] - additional data used to filter pages and metrics in the portal. Defaults to empty.
   * @param   measurements    map[string, number] - metrics associated with this page, displayed in Metrics Explorer on the portal. Defaults to empty.
   */
  stopTrackPage(
    name?: string,
    url?: string,
    additionalProperties?: { [p: string]: string },
    measurements?: {
      [p: string]: number;
    },
  ): void {
    try {
      const properties: { [name: string]: string } = {
        ...this._properties,
        ...additionalProperties,
      };
      this.appInsights.stopTrackPage(name, url, properties, measurements);
      console.log('stop tracking page:: ' + name);
    } catch (ex) {
      console.warn('Angular application insights Error [stopTrackPage]: ', ex);
    }
  }

  /**
   * Start timing an extended event. Call {@link stopTrackEvent} to log the event when it ends.
   * @param   name    A string that identifies this event uniquely within the document.
   */
  startTrackEvent(name: string): void {
    try {
      this.appInsights.startTrackEvent(name);
      console.log('start tracking:: ' + name);
    } catch (ex) {
      console.warn(
        'Angular application insights Error [startTrackEvent]: ',
        ex,
      );
    }
  }

  /**
   * Log an extended event that you started timing with {@link startTrackEvent}.
   * @param   name    The string you used to identify this event in startTrackEvent.
   * @param   additionalProperties  map[string, string] - additional data used to filter events and metrics in the portal. Defaults to empty.
   * @param   measurements    map[string, number] - metrics associated with this event, displayed in Metrics Explorer on the portal. Defaults to empty.
   */
  stopTrackEvent(
    name: string,
    additionalProperties?: { [p: string]: string },
    measurements?: { [p: string]: number },
  ): void {
    try {
      const properties: { [name: string]: string } = {
        ...this._properties,
        ...additionalProperties,
      };
      this.appInsights.stopTrackEvent(name, properties, measurements);
      console.log('stop tracking:: ' + name);
    } catch (ex) {
      console.warn('Angular application insights Error [stopTrackEvent]: ', ex);
    }
  }

  // https://github.com/Microsoft/ApplicationInsights-JS/blob/master/API-reference.md#trackmetric
  /**
   * Log a numeric value that is not associated with a specific event. Typically used
   * to send regular reports of performance indicators.
   *
   * To send a single measurement, just use the `name` and `average` fields
   * of {@link IMetricTelemetry}.
   *
   * If you take measurements frequently, you can reduce the telemetry bandwidth by
   * aggregating multiple measurements and sending the resulting average and modifying
   * the `sampleCount` field of {@link IMetricTelemetry}.
   * @param {IMetricTelemetry} metric input object argument. Only `name` and `average` are mandatory.
   * @param {ICustomProperties} [customProperties]
   * @memberof Initialization
   */
  trackMetric(
    name: string,
    value: number,
    additionalProperties?: { [p: string]: string },
  ): void {
    try {
      const metric = { name: name, average: value };
      const properties: { [name: string]: string } = {
        ...this._properties,
        ...additionalProperties,
      };
      this.appInsights.trackMetric(metric, properties);
    } catch (ex) {
      console.warn('Angular application insights Error [trackTrace]: ', ex);
    }
  }

  // https://github.com/Microsoft/ApplicationInsights-JS/blob/master/API-reference.md#trackexception
  /**
   * Log an exception that you have caught.
   * @param {IExceptionTelemetry} exception
   * @memberof Initialization
   */
  trackException(
    exception: Error,
    severityLevel?: SeverityLevel,
    additionalProperties?: { [name: string]: string },
  ): void {
    try {
      const properties: { [name: string]: string } = {
        ...this._properties,
        ...additionalProperties,
      };
      const ex = {
        exception: exception,
        severityLevel: severityLevel,
        properties: properties,
      };
      this.appInsights.trackException(ex);
    } catch (ex) {
      console.warn('Angular application insights Error [trackException]: ', ex);
    }
  }

  private getActivatedComponent(snapshot: ActivatedRouteSnapshot): any {
    if (snapshot.firstChild) {
      return this.getActivatedComponent(snapshot.firstChild);
    }

    return snapshot.component;
  }

  private getRouteTemplate(snapshot: ActivatedRouteSnapshot): string {
    let path = '';
    if (snapshot.routeConfig) {
      path += snapshot.routeConfig.path;
    }

    if (snapshot.firstChild) {
      return path + this.getRouteTemplate(snapshot.firstChild);
    }

    return path;
  }

  public logError(error: Error | HttpErrorResponse): void {
    if (this.appInsights) {
      this.appInsights.trackException({ exception: error });
    }
  }
}